/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router';
import { Avatar, Breadcrumbs, Divider, Paper, Theme, useTheme } from '@material-ui/core';
import ConnectorDealFinancePanel from './ConnectorDealFinancePanel';
import ConnectorDealButtonsBar from './ConnectorDealButtonsBar';
import IndustryBullet from '../../../components/IndustryBullet';
import { StyledText } from '../../../components/StyledText';
import { ConnectorDeal, Orientation, Shape } from '../../../types/types';
import { useConnector } from '../../../contexts';
import { getDisplayedLocation, getYear5Revenue } from '../../../lib';
import Placeholder, { PlaceholderType } from '../../../components/Placeholder';
import DetectLinks from '../../../components/DetectLinks';


const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {
    paddingLeft: '0px',
    paddingRight: '0px'
  },
  cardSheet: {
    backgroundColor: theme.palette.background.paper,
    margin: '24px 0'
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column'
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row'
    }
  },
  formControl: {
    width: '100%'
  },
  stateFormControl: {
    width: '100%',
    marginTop: theme.spacing(1)
  },
  table: {
    display: 'block',
    [theme.breakpoints.down('md')]: {
      maxHeight: 'auto'
    },
    [theme.breakpoints.up('md')]: {
      maxHeight: 450
    }
  },
  container: {
    position: 'relative'
  },
  headerLeftSide: () => ({
    [theme.breakpoints.down('md')]: {
      width: 'auto'
    },
    [theme.breakpoints.up('md')]: {
      width: '50%'
    },
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  }),
  headerRightSide: () => ({
    [theme.breakpoints.down('md')]: {
      display: 'none'
    },
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-end',
      width: 'auto'
    }
  }),
  logoImage: {
    width: 'auto',
    height: 'auto',
    maxHeight: 60,
    objectFit: 'contain'
  },
  leftPanel: () => ({
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      padding: theme.spacing(4)
    },
    [theme.breakpoints.up('md')]: {
      width: '65%',
      padding: theme.spacing(4)
    },
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  }),

  leftPanelContainer: {
    display: 'flex',
    width: '100%'
  },

  rightPanel: () => ({
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      padding: theme.spacing(4)
    },
    [theme.breakpoints.up('md')]: {
      width: '30%',
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    },
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column'
  }),
  title: {
    color: theme.palette.text.primary,
    marginTop: '10px',
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.main
    }
  },
  subTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  managementItem: {
    display: 'flex',
    flexFlow: 'row wrap',
    alignItems: 'center',
    '& > .mngItemDescription': {
      margin: '0 10px'
    }
  },
  divider: {
    width: '100%',
    padding: 0
  },
  industryListContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '7px 0px 0px 0px'
  }
}));

interface ConnectorDealHolderProps {
  renderRightPanelContent: (deal: ConnectorDeal) => React.ReactNode;
  handleReferralClick: (deal: ConnectorDeal) => void;
  deal: ConnectorDeal;
}

export default function ConnectorDealHolder({ renderRightPanelContent, deal, handleReferralClick }: ConnectorDealHolderProps) {
  const theme = useTheme();
  const classes = useStyles(theme);

  const { canSendReferrals } = useConnector();
  const history = useHistory();

  const dealManagement = useMemo(() => (deal.contactTitle ? `${deal.contactName} - ${deal.contactTitle}` : deal.contactName), [deal]);
  const rightPanel = useMemo(() => (renderRightPanelContent(deal)), [deal, renderRightPanelContent]);

  const { referralRequestedDate } = deal;

  const goToEntProfile = (profileId: number) => {
    history.push(`/entrepreneur/${profileId}`);
  };


  return (
    <Paper className={classes.cardSheet} key={deal.id}>
      <div className={classes.content}>
        <div className={classes.leftPanel}>
          <div className={classes.leftPanelContainer}>
            <div className={classes.headerLeftSide}>
              <Breadcrumbs separator='|' aria-label='breadcrumb'>
                <StyledText color='primary' variant='body1' style={{ textTransform: 'capitalize' }}>{getDisplayedLocation(deal.geography)}</StyledText>
                <StyledText color='primary' variant='body1'>{deal.marketModelList.join(', ')}</StyledText>
              </Breadcrumbs>
              <StyledText component='div' variant='h2' className={classes.title} onClick={() => { goToEntProfile(deal.entrepreneurProfileId); }}>
                {deal.companyName}
              </StyledText>
            </div>
            <div className={classes.headerRightSide}>
              {deal.logo
                ? <img src={deal.logo} alt='logo' className={classes.logoImage} />
                : <Placeholder type={PlaceholderType.Image} shape={Shape.Rect} orientation={Orientation.Horizontal} />}
            </div>
          </div>
          <StyledText className={classes.subTitle} variant='body3' color='textSecondary'>
            <DetectLinks content={deal.intro || ''} />
          </StyledText>
          <div className={classes.managementItem}>
            <Avatar src={deal.profileImage} />
            <StyledText
              className='mngItemDescription'
              variant='body1'
              color='textPrimary'
              component='div'
            >
              {dealManagement}
            </StyledText>
          </div>
          <div className={classes.industryListContainer}>
            {deal.industryList.map((industry: string) => <IndustryBullet key={`${deal.id}_${industry}`} industry={industry} padding='10px 22px 10px 0' />)}
          </div>
          <ConnectorDealFinancePanel
            financeRaised={deal.financeRaised}
            financeSoughtHigh={deal.financeSoughtHigh}
            financeSoughtLow={deal.financeSoughtLow}
            currentRevenue={deal.currentRevenue}
            fiveYearsFinProjection={getYear5Revenue(deal.financialProjection)}
          />
          {
            referralRequestedDate
            && (
            <StyledText color='textSecondary' style={{ marginTop: '32px' }}>
              {`Referral requested ${referralRequestedDate}`}
            </StyledText>
            )
          }
        </div>
        <div className={classes.rightPanel}>
          {rightPanel}
        </div>
      </div>
      <Divider className={classes.divider} style={{ marginBottom: '0px' }} />
      <ConnectorDealButtonsBar
        deal={deal}
        handleReferralClick={canSendReferrals ? handleReferralClick : undefined}
      />
    </Paper>
  );
}
